import { useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import useAuthStore from "@/store/use-auth-store";
import { useSiteConfigs } from "@/features/ai-model-selection/hooks/use-site-configs";
import { useAgreementsStore } from "@/features/agreements";
import { useFlag } from "@/lib/use-flag";
import { isPersonaSetupDismissed } from "../lib/session-skip";
import { usePersonaSetupStore } from "../store/persona-setup-store";

export const PERSONA_SETUP_PATH = "/persona-setup";
export const ONBOARDING_PATH = "/onboarding";

/**
 * Bir flag yüzünden atlanan adımı oturumda bir kez raporla.
 *
 * Gerekli, çünkü basit bir force kuralı (ör. ülke = US) deney değil — GrowthBook
 * `trackingCallback`'i tetiklemez ve Mixpanel'de kimsenin atlandığına dair hiçbir
 * iz kalmaz. Set modül seviyesinde: bu hook aynı anda birden fazla guard'da
 * (PrivateRoute, OptionalAuthRoute...) mount oluyor, aksi halde event çoğalır.
 */
const trackedSkips = new Set<string>();

const trackFlagSkipOnce = (step: string, flag: string) => {
  if (trackedSkips.has(flag)) return;
  trackedSkips.add(flag);

  try {
    mixpanel.track("onboarding_gate_skipped", {
      step,
      flag,
      reason: "flag_off",
    });
  } catch (e) {
    console.warn("Mixpanel error:", e);
  }
};

const hasUnconfirmedSiteAgreements = (
  siteAgreements: Array<{ name: string; version: string }> | undefined,
  confirmations:
    | Array<{ agreement: { name: string }; version: string }>
    | undefined
    | null
): boolean => {
  if (!siteAgreements || siteAgreements.length === 0) return false;
  // Profil kaydı /me öncesi confirmations silinmiş gibi görünebilir;
  // undefined'ı "hepsi bekliyor" sanmayalım.
  if (!confirmations) return false;
  return siteAgreements.some((siteAgreement) => {
    const match = confirmations.find(
      (entry) => entry.agreement.name === siteAgreement.name
    );
    if (!match) return true;
    return (
      new Date(siteAgreement.version).getTime() >
      new Date(match.version).getTime()
    );
  });
};

/**
 * Login sonrası sıra: sözleşmeler → persona quest → gamification.
 * Karar yalnızca `/me`.gate — progress / try-on / site-configs beklenmez.
 */
export const usePostLoginGate = () => {
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);
  const userId = user?._id;
  const gate = user?.gate;

  const pendingAgreements = useAgreementsStore(
    (state) => state.pendingAgreements
  );
  const isAgreementModalOpen = useAgreementsStore(
    (state) => state.isAgreementModalOpen
  );

  const siteConfigs = useSiteConfigs({ enabled: !!userId });

  const hasPendingAgreements =
    hasUnconfirmedSiteAgreements(
      siteConfigs.data?.data?.agreements,
      user?.agreementConfirmations
    ) ||
    pendingAgreements.length > 0 ||
    isAgreementModalOpen;

  // /me fail olursa gate hiç gelmez; 8 sn sonra splash'i bırak (quest zorlama).
  const [gateTimedOut, setGateTimedOut] = useState(false);
  useEffect(() => {
    if (gate != null) {
      setGateTimedOut(false);
      return;
    }
    if (!accessToken) return;
    const timer = window.setTimeout(() => setGateTimedOut(true), 8000);
    return () => window.clearTimeout(timer);
  }, [accessToken, gate]);

  const isPersonaSetupEnabled = useFlag("web-persona-setup-enabled", true);
  const isSelfieOnboardingEnabled = useFlag(
    "web-selfie-onboarding-enabled",
    true
  );

  const isGateLoading = !!accessToken && !!userId && gate == null && !gateTimedOut;

  const dismissedUserId = usePersonaSetupStore((state) => state.dismissedUserId);
  const sessionSkip =
    !!userId &&
    (dismissedUserId === userId || isPersonaSetupDismissed(userId));

  const shouldShowPersonaSetup =
    isPersonaSetupEnabled &&
    !!gate?.needsPersonaSetup &&
    !sessionSkip &&
    !hasPendingAgreements &&
    !gateTimedOut;

  const shouldShowOnboarding = !!gate?.needsOnboarding;

  useEffect(() => {
    if (!userId || isGateLoading) return;

    if (!isPersonaSetupEnabled && gate?.needsPersonaSetup) {
      trackFlagSkipOnce("persona_setup", "web-persona-setup-enabled");
    }
    if (!isSelfieOnboardingEnabled && shouldShowOnboarding) {
      trackFlagSkipOnce("selfie_onboarding", "web-selfie-onboarding-enabled");
    }
  }, [
    userId,
    isGateLoading,
    isPersonaSetupEnabled,
    isSelfieOnboardingEnabled,
    gate?.needsPersonaSetup,
    shouldShowOnboarding,
  ]);

  return {
    isGateLoading,
    hasPendingAgreements,
    shouldShowPersonaSetup,
    shouldShowOnboarding: isSelfieOnboardingEnabled && shouldShowOnboarding,
    isPersonaComplete: gate != null && !gate.needsPersonaSetup,
  };
};

/** Guard'lar için ortak sıra: sözleşmeler → persona → gamification */
export const resolvePostLoginRedirect = (
  pathname: string,
  gate: ReturnType<typeof usePostLoginGate>
): "loading" | string | null => {
  // Quest sayfası mount kalsın — adım kaydı gate'i titretip /'e atmasın.
  if (pathname === PERSONA_SETUP_PATH) return null;

  if (gate.isGateLoading) return "loading";
  if (gate.hasPendingAgreements) return null;
  if (gate.shouldShowPersonaSetup) {
    return PERSONA_SETUP_PATH;
  }
  if (gate.shouldShowOnboarding && pathname !== ONBOARDING_PATH) {
    return ONBOARDING_PATH;
  }
  return null;
};
